/**
 * @Module: Ingredient Categories
 * @Status: Ready
 * @Summary: Shared store-section definitions and categorization for shopping lists
 * @Description: Extracts the duplicated SECTIONS array and categorize() function
 *   from ShoppingList.tsx and MealPlanShoppingList.tsx into one shared module.
 *   Keyword matching is longest-first so "sour cream" beats "cream".
 * @NextSteps: Add user-configurable category overrides
 */

import type { IconName } from '../components/icons/AppIcon';

// --- Section definition ---

export interface SectionDef {
  key: string;
  label: string;
  icon: IconName;
  color: string;
  keywords: string[];
}

export const SECTIONS: SectionDef[] = [
  {
    key: 'produce', label: 'Produce', icon: 'shop.produce', color: 'green.500',
    keywords: [
      'tomato', 'onion', 'garlic', 'pepper', 'lettuce', 'basil', 'cilantro',
      'lemon', 'lime', 'avocado', 'potato', 'carrot', 'celery', 'mushroom',
      'spinach', 'kale', 'ginger', 'cucumber', 'zucchini', 'broccoli', 'corn',
      'parsley', 'mint', 'dill', 'scallion', 'shallot', 'asparagus', 'cabbage',
      'beet', 'radish', 'eggplant', 'squash', 'peas', 'beans', 'leek',
    ],
  },
  {
    key: 'dairy', label: 'Dairy & Eggs', icon: 'shop.dairy', color: 'blue.400',
    keywords: [
      'milk', 'cream', 'butter', 'cheese', 'yogurt', 'egg', 'sour cream',
      'mozzarella', 'parmesan', 'cheddar', 'ricotta', 'buttermilk',
    ],
  },
  {
    key: 'meat', label: 'Meat & Seafood', icon: 'shop.meat', color: 'red.400',
    keywords: [
      'chicken', 'beef', 'pork', 'fish', 'shrimp', 'salmon', 'turkey',
      'bacon', 'ham', 'sausage', 'lamb', 'steak', 'prawn', 'tuna', 'crab',
    ],
  },
  {
    key: 'spice', label: 'Spices & Seasonings', icon: 'shop.spice', color: 'orange.400',
    keywords: [
      'salt', 'pepper', 'oregano', 'basil', 'cumin', 'paprika', 'cinnamon',
      'turmeric', 'chili', 'thyme', 'rosemary', 'cayenne', 'nutmeg',
      'garlic powder', 'onion powder', 'cardamom', 'coriander', 'allspice',
      'bay leaf', 'red pepper flakes', 'vanilla', 'extract',
    ],
  },
  {
    key: 'pantry', label: 'Pantry', icon: 'shop.pantry', color: 'yellow.600',
    keywords: [
      'flour', 'sugar', 'oil', 'olive oil', 'vinegar', 'soy sauce', 'pasta',
      'rice', 'bread', 'noodles', 'broth', 'stock', 'tomato sauce', 'coconut milk',
      'honey', 'maple syrup', 'baking soda', 'baking powder', 'chocolate',
      'vanilla', 'cornstarch', 'ketchup', 'mustard', 'tortilla', 'oats',
      'breadcrumb', 'panko', 'wine', 'water',
    ],
  },
  {
    key: 'other', label: 'Other', icon: 'shop.other', color: 'gray.500',
    keywords: [],
  },
];

/**
 * Categorize an ingredient name into a store section key.
 * Matches longest keyword first so "sour cream" wins over "cream".
 */
export function categorize(name: string): string {
  const lower = name.toLowerCase();
  for (const section of SECTIONS) {
    const sorted = [...section.keywords].sort((a, b) => b.length - a.length);
    for (const kw of sorted) {
      if (lower.includes(kw)) return section.key;
    }
  }
  return 'other';
}
